import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import { FormText } from 'reactstrap';
import {
  inputPropTypes,
  metaPropTypes,
  inputDefaultProps,
  metaDefaultProps,
} from '../../forms/input/InputPropTypes';
import { optionsPropTypes } from './SelectPropTypes';
import { debounce } from '../../../../utils/promise';

const tagWithUid = (selectedValue) => {
  if (!Array.isArray(selectedValue)) return selectedValue;

  const occurrences = new Map();

  return selectedValue.map((option) => {
    if (!option) return option;
    const count = occurrences.get(option.value) || 0;
    occurrences.set(option.value, count + 1);
    return { ...option, __uid: `${option.value}__${count}` };
  });
};

const FormSelect = ({
  input: { onChange, value },
  name,
  options,
  isLoading,
  isClearable,
  isMulti,
  onInputChange,
  placeholder,
  isDisabled,
  meta: { touched, error, warning },
  autoFocus,
  noOptionsMessage,
  filterOption,
  setFieldToBeFocused,
  closeMenuOnSelect,
  onValueChange,
  allowRepeatedOptions,
}) => {
  const handleChange = (selectedOption) => {
    if (onChange) onChange(selectedOption);
    if (onValueChange) onValueChange(selectedOption);
  };

  const displayValue =
    isMulti && allowRepeatedOptions ? tagWithUid(value) : value;

  const props = {
    isDisabled,
    onChange: handleChange,
    onInputChange: debounce(onInputChange),
    value: displayValue,
    name,
    options,
    isClearable,
    isMulti,
    isLoading,
    placeholder,
    autoFocus,
    noOptionsMessage,
    closeMenuOnSelect,
    loadingMessage: () => 'Cargando...',
  };

  if (allowRepeatedOptions) {
    props.hideSelectedOptions = false;
    props.isOptionSelected = () => false;
    props.getOptionValue = (option) =>
      // eslint-disable-next-line
      option.__uid ? option.__uid : option.value;
  }

  if (setFieldToBeFocused)
    props.ref = (element) => setFieldToBeFocused(element);

  if (filterOption) props.filterOption = filterOption;

  return (
    <div>
      <ReactSelect {...props} />
      <FormText color="danger">
        {touched &&
          ((error && <span>{error}</span>) ||
            (warning && <span>{warning}</span>))}
      </FormText>
    </div>
  );
};

FormSelect.propTypes = {
  input: inputPropTypes,
  name: PropTypes.string,
  onInputChange: PropTypes.func,
  placeholder: PropTypes.string,
  options: optionsPropTypes,
  isLoading: PropTypes.bool,
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  meta: metaPropTypes,
  autoFocus: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  filterOption: PropTypes.func,
  setFieldToBeFocused: PropTypes.func,
  closeMenuOnSelect: PropTypes.bool,
  onValueChange: PropTypes.func,
  allowRepeatedOptions: PropTypes.bool,
};

FormSelect.defaultProps = {
  name: '',
  options: [],
  input: inputDefaultProps,
  meta: metaDefaultProps,
  onInputChange: () => {},
  isLoading: false,
  isClearable: false,
  isMulti: false,
  isDisabled: false,
  placeholder: 'Selecciona ...',
  autoFocus: false,
  noOptionsMessage: () => 'No hay resultados.',
  filterOption: null,
  setFieldToBeFocused: null,
  closeMenuOnSelect: true,
  onValueChange: undefined,
  allowRepeatedOptions: false,
};

export default FormSelect;
